import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import Image from '../../../components/AppImage';

const TechnologyPartnersSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.3 });

  const partners = [
    {
      id: 1,
      name: "Amazon Web Services",
      logo: "https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=200&h=100&fit=crop",
      category: "Cloud Infrastructure",
      description: "Certified AWS Partner providing scalable cloud solutions"
    },
    {
      id: 2,
      name: "Microsoft Azure",
      logo: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=200&h=100&fit=crop",
      category: "Cloud Platform",
      description: "Azure certified for enterprise cloud transformations"
    },
    {
      id: 3,
      name: "Google Cloud",
      logo: "https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?w=200&h=100&fit=crop",
      category: "AI & Analytics",
      description: "Google Cloud Partner specializing in AI and data analytics"
    },
    {
      id: 4,
      name: "Salesforce",
      logo: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=100&fit=crop",
      category: "CRM Solutions",
      description: "Salesforce consulting partner for CRM implementations"
    },
    {
      id: 5,
      name: "MongoDB",
      logo: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=200&h=100&fit=crop",
      category: "Database",
      description: "MongoDB partner for modern database solutions"
    },
    {
      id: 6,
      name: "Docker",
      logo: "/assets/images/docker.png",
      category: "DevOps",
      description: "Container technology and DevOps automation expertise"
    }
  ];

  const certifications = [
    {
      id: 1,
      name: "ISO 27001",
      description: "Information Security Management",
      icon: "Shield"
    },
    {
      id: 2,
      name: "SOC 2 Type II",
      description: "Security & Availability",
      icon: "CheckCircle"
    },
    {
      id: 3,
      name: "GDPR Compliant",
      description: "Data Protection Standards",
      icon: "Lock"
    },
    {
      id: 4,
      name: "PCI DSS",
      description: "Payment Card Industry Standards",
      icon: "CreditCard"
    }
  ];

  return (
    <section ref={ref} className="py-20 lg:py-32 bg-muted/20 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-tr from-accent/10 to-transparent" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Technology
            <span className="text-accent block lg:inline lg:ml-4">Partners</span>
          </h2>
          <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            We partner with industry-leading technology companies to deliver modern solutions and maintain the highest standards of security and compliance.
          </p>
        </motion.div>

        {/* Partners Grid */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {partners?.map((partner, index) => (
            <motion.div
              key={partner?.id}
              className="group bg-card rounded-2xl p-6 lg:p-8 border border-border hover:border-accent/50 transition-all duration-300 hover:shadow-lg hover:shadow-accent/10"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
            >
              {/* Partner Logo */}
              <div className="w-full h-24 mb-6 rounded-lg overflow-hidden bg-background/50 flex items-center justify-center">
                <Image
                  src={partner?.logo}
                  alt={`${partner?.name} logo`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 grayscale group-hover:grayscale-0"
                />
              </div>

              {/* Partner Info */}
              <div className="text-center">
                <h3 className="text-lg lg:text-xl font-semibold text-card-foreground mb-2">
                  {partner?.name}
                </h3>
                <div className="text-accent text-sm font-medium mb-3">
                  {partner?.category}
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {partner?.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Certifications Section */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-12">
            Security & <span className="text-accent">Compliance</span>
          </h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {certifications?.map((cert, index) => (
              <motion.div
                key={cert?.id}
                className="bg-card rounded-xl p-6 border border-border hover:border-accent/50 transition-all duration-300"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
              >
                <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-6 h-6 bg-accent rounded-full" />
                </div>
                <h4 className="font-semibold text-card-foreground mb-2">
                  {cert?.name}
                </h4>
                <p className="text-muted-foreground text-sm">
                  {cert?.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          className="text-center mt-16 lg:mt-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <div className="bg-gradient-to-r from-accent/10 to-accent/5 rounded-2xl p-8 lg:p-12 border border-accent/20">
            <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              Ready to Transform Your Business?
            </h3>
            <p className="text-text-secondary text-lg mb-8 max-w-2xl mx-auto">
              Let's discuss how our expertise and technology partnerships can help you achieve your digital transformation goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="/contact"
                className="bg-accent text-accent-foreground px-8 py-3 rounded-lg font-medium hover:bg-accent/90 transition-all duration-200 hover:scale-105"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started Today
              </motion.a>
              <motion.a
                href="/services"
                className="border border-accent text-accent px-8 py-3 rounded-lg font-medium hover:bg-accent/10 transition-all duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                View Our Services
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TechnologyPartnersSection;